import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Target, Heart, Rocket, Cpu, Zap, Shield } from "lucide-react";

const values = [
  {
    icon: Target,
    title: "Missão",
    description:
      "Acabar com a dor de cabeça de contratar influencer. Democratizar publis de qualidade pra qualquer marca, do MEI ao enterprise.",
  },
  {
    icon: Heart,
    title: "Valores",
    description:
      "Transparência radical, inovação sem frescura, e um compromisso inabalável em fazer você vender mais (sem drama).",
  },
  {
    icon: Rocket,
    title: "Visão",
    description:
      "Ser a ferramenta que todo marqueteiro usa quando pensa 'preciso de uma publi'. Simples assim.",
  },
];

const technology = [
  {
    icon: Cpu,
    title: "IA que Manja",
    description:
      "Modelos de última geração que criam personas e roteiros tão bons que você vai querer contratar a IA de verdade.",
  },
  {
    icon: Zap,
    title: "Rápido Demais",
    description:
      "Enquanto você lê isso, alguém já gerou 3 publis. Nossa infra processa tudo na nuvem, sem travar seu celular.",
  },
  {
    icon: Shield,
    title: "Seus Dados, Sua Paz",
    description:
      "Segurança level banco. Seus produtos e dados ficam protegidos como o segredo da fórmula da Coca.",
  },
];

const stats = [
  { value: "2024", label: "Fundação" },
  { value: "3", label: "Publis por Crédito" },
  { value: "9:16", label: "Formato Pro" },
  { value: "~5min", label: "Tempo de Geração" },
];

export default function Sobre() {
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--yellow)/0.1),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Sobre o <span className="text-gradient">Publee</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              A gente cansou de ver marca gastando fortunas com influencer pra receber um post meia-boca. 
              Então decidimos resolver isso.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-2xl gradient-card border border-border"
              >
                <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Nossa <span className="text-gradient">História</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Tudo começou quando a gente viu uma marca pagar R$15k num publi 
                  que nem mencionava o produto direito. O influencer? Sumiu depois do pix.
                </p>
                <p>
                  "Tem que ter um jeito melhor", a gente pensou. E com a evolução 
                  da IA generativa, percebemos que dava pra criar publis tão boas 
                  (ou melhores) sem depender de ninguém.
                </p>
                <p>
                  Hoje o Publee permite que qualquer pessoa transforme fotos de 
                  produtos em vídeos profissionais em minutos. Sem negociação, 
                  sem drama, sem surpresas desagradáveis.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl gradient-card border border-border overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Cpu className="w-32 h-32 text-primary/20" />
                </div>
              </div>
              <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full rounded-3xl bg-primary/10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              O que nos <span className="text-gradient">move</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl gradient-card border border-border"
              >
                <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Nossa <span className="text-gradient">Tecnologia</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Por trás de cada publi tem muita engenharia rolando. Mas você não precisa saber disso.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {technology.map((tech, index) => (
              <motion.div
                key={tech.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="p-6 rounded-xl bg-secondary/50 border border-border"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <tech.icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">{tech.title}</h4>
                <p className="text-sm text-muted-foreground">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
